// One setting row's value, edited the way its shape calls for, and its change history.
// Shared by the Settings screen and the list that lives under Library (catalog families).

import { useState } from 'react';
import { updateRow, useRows, type Row } from '../lib/api';
import { longDate } from '../lib/format';
import { useSession } from '../lib/session';
import { Btn, Edit, Loading } from './kit';
import { Grip, useDragOrder } from './move';

/** What a list of names may do beyond saving the list: rename through its own step, or ask before a removal. */
export interface ListHooks {
  /** Rename one name. When given, it replaces the plain save of the renamed list (a family renames its items too). */
  rename?: (from: string, to: string) => Promise<unknown>;
  /** Asked before a name is removed; false keeps it. */
  beforeRemove?: (name: string) => Promise<boolean> | boolean;
  /** What one name is called: "family", "state". */
  one?: string;
}

/** A list of names: one row each, renamed in place, dragged into order by its grip, removed, and added at the bottom. The order is the order on screen. */
export function ListEdit({ value, onSave, hooks, disabled }: { value: string[]; onSave: (v: string[]) => Promise<unknown>; hooks?: ListHooks; disabled?: boolean }) {
  const [adding, setAdding] = useState('');
  const one = hooks?.one ?? 'name';
  const has = (name: string, except?: string) => value.some((x) => x !== except && x.toLowerCase() === name.toLowerCase());
  const drag = useDragOrder((id, target) => {
    const from = value.indexOf(id);
    const to = value.indexOf(target);
    if (from < 0 || to < 0) return;
    const next = [...value];
    const [name] = next.splice(from, 1);
    next.splice(to, 0, name!);
    return onSave(next);
  }, disabled);
  const rename = (from: string, to: string) => {
    if (hooks?.rename) return hooks.rename(from, to);
    return onSave(value.map((x) => (x === from ? to : x)));
  };
  const remove = async (name: string) => {
    // a list that has its own question asks it; any other asks before a name goes (design review D6)
    if (hooks?.beforeRemove) {
      if (!(await hooks.beforeRemove(name))) return;
    } else if (!window.confirm(`Remove the ${one} "${name}" from the list?`)) return;
    await onSave(value.filter((x) => x !== name));
  };
  const add = () => {
    const name = adding.trim();
    if (!name) return;
    if (has(name)) {
      window.alert(`"${name}" is already on the list.`);
      return;
    }
    setAdding('');
    void onSave([...value, name]);
  };
  return (
    <div className="listedit">
      {value.length === 0 && <small>Nothing on the list yet.</small>}
      {value.map((name) => (
        <div key={name} style={{ display: 'flex', gap: 6, padding: '1px 0', flexWrap: 'nowrap', alignItems: 'center' }} {...drag.row(name, 'row mid dragrow')}>
          {!disabled && <Grip {...drag.grip(name)} />}
          <Edit className="cell grow" disabled={disabled} value={name} parse={(t) => (t.trim() && !has(t.trim(), name) ? t.trim() : undefined)} onSave={(v) => rename(name, v)} />
          {!disabled && (
            <>
              <Btn small title={`Remove this ${one}`} onClick={() => void remove(name)}>
                ×
              </Btn>
            </>
          )}
        </div>
      ))}
      {!disabled && (
        <div className="row mid" style={{ gap: 6, marginTop: 4 }}>
          <input
            className="cell grow"
            placeholder={`New ${one}`}
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') add();
            }}
          />
          <Btn small onClick={add} disabled={!adding.trim()}>
            + Add {one}
          </Btn>
        </div>
      )}
    </div>
  );
}

/** An editor shaped by the value: a switch, a number, a list of names, or structured text. */
export function ValueEdit({ value, onSave, hooks }: { value: unknown; onSave: (v: unknown) => Promise<unknown>; hooks?: ListHooks }) {
  if (typeof value === 'boolean') {
    return (
      <button type="button" className={`tag${value ? ' on' : ''}`} onClick={() => void onSave(!value)}>
        {value ? 'On · showing' : 'Off · hidden'}
      </button>
    );
  }
  if (typeof value === 'number') {
    return <Edit className="short" value={value} parse={(t) => (/^-?\d*\.?\d+$/.test(t.trim()) ? t.trim() : undefined)} onSave={(v) => onSave(Number(v))} />;
  }
  if (Array.isArray(value) && value.every((x) => typeof x === 'string')) {
    return <ListEdit value={value as string[]} onSave={onSave} hooks={hooks} />;
  }
  if (value && typeof value === 'object' && !Array.isArray(value) && Object.values(value).every((x) => typeof x === 'number' || typeof x === 'string')) {
    const obj = value as Record<string, number | string>;
    return (
      <div>
        {Object.entries(obj).map(([k, v]) => (
          <div key={k} className="row mid" style={{ justifyContent: 'space-between', gap: 8 }}>
            <small>{k.replace(/_/g, ' ')}</small>
            <Edit className="cell short right" value={v} onSave={(t) => onSave({ ...obj, [k]: typeof v === 'number' ? Number(t) : t })} parse={(t) => (typeof v === 'number' ? (/^-?\d*\.?\d+$/.test(t.trim()) ? t.trim() : undefined) : t)} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div>
      <Edit
        className="cell"
        value={JSON.stringify(value)}
        parse={(t) => {
          try {
            JSON.parse(t);
            return t;
          } catch {
            return undefined;
          }
        }}
        onSave={(v) => onSave(JSON.parse(v))}
      />
      <small>Structured value. Keep the brackets and quotes as they are and change the words or numbers inside.</small>
    </div>
  );
}

export function History({ table, rowId, users }: { table: string; rowId: string; users: Row[] }) {
  const audit = useRows('audit', { eq: { table_name: table, row_id: rowId }, order: ['-changed_at'] });
  if (audit.loading) return <Loading />;
  if (audit.rows.length === 0) return <small>No changes since it was loaded.</small>;
  return (
    <div style={{ marginTop: 6 }}>
      {audit.rows.slice(0, 10).map((a) => (
        <div key={a.id}>
          <small>
            {longDate(a.changed_at)} · {users.find((u) => u.id === a.changed_by)?.name ?? 'the seed or an AI employee'} · was {JSON.stringify(a.before?.value)}
          </small>
        </div>
      ))}
    </div>
  );
}

/** The setting rows named by key, each with its editor: a list that belongs on another screen (catalog families under Library). */
export function SettingRows({ keys, disabled, hooks, bare }: { keys: string[]; disabled?: boolean; hooks?: Record<string, ListHooks>; /** The panel already names the one list shown, so its label line is left out. */ bare?: boolean }) {
  const { user } = useSession();
  const settings = useRows('setting', { order: ['group', 'label'] });
  const shown = keys.map((k) => settings.rows.find((r) => r.key === k)).filter((r): r is Row => !!r);
  if (settings.loading) return <Loading />;
  return (
    <>
      {shown.map((r) => (
        <div key={r.id} className="ln" style={{ display: 'block' }}>
          {!bare && (
            <div className="row mid" style={{ justifyContent: 'space-between' }}>
              <b>{r.label}</b>
              <small>last changed {longDate(r.updated_at)}</small>
            </div>
          )}
          {disabled && Array.isArray(r.value) ? (
            <ListEdit value={r.value as string[]} onSave={() => Promise.resolve()} disabled />
          ) : disabled ? (
            <span>{JSON.stringify(r.value)}</span>
          ) : (
            <ValueEdit value={r.value} hooks={hooks?.[r.key as string]} onSave={(v) => updateRow('setting', r.id, { value: v, updated_by: user?.id ?? null })} />
          )}
        </div>
      ))}
    </>
  );
}
